import { useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { LoadingIndicator, Pagination } from 'components/common';
import {
  PageEmptyIndicator,
  PageSectionHeader,
  PageShowAllLink
} from 'components/page';
import { CalendarEventAdminTile, CalendarEventTile } from '.';

import type { GetCalendarEventsMinimalRes } from 'shared-types/responses';
import type { ViewProps } from 'types';

const CalendarEventsViewAllCalendarEvents = ({
  description,
  hideIfNoData = false,
  isHomeView = false,
  limitPerPage = 8,
  onNoData = null,
  queryParams = '',
  showAdminTiles = true,
  showAllLinkHref = null,
  title,
  titleShowCount = true
}: ViewProps & { showAdminTiles?: boolean }) => {
  const { messages, user } = useReduxState();

  const [page, setPage] = useState(1);

  const { data, loading } = useApiData<GetCalendarEventsMinimalRes>({
    url: `/admin/calendar-events?minimal=true&limit=${limitPerPage}&page=${page}${queryParams}`,
    onlyIf: Boolean(user)
  });

  useEffect(() => {
    setPage(1);
  }, [queryParams]);

  useEffect(() => {
    const hasData = !loading && Boolean(data?.events?.length);
    if (!loading && onNoData) onNoData(hasData);
  }, [data, loading]);

  return loading ? (
    <LoadingIndicator />
  ) : data?.eventsCount > 0 ? (
    <div className="content">
      <PageSectionHeader
        description={description}
        title={title || messages.commonAllCalendarEvents}
        titleCount={titleShowCount ? data?.eventsCount || 0 : undefined}
      />
      <div className="events">
        {data.events.map((event) => (
          <div className="event" key={event.id}>
            {showAdminTiles &&
            event.owners?.some((owner) => owner.id === user?.id) ? (
              <CalendarEventAdminTile event={event} key={event.id} />
            ) : (
              <CalendarEventTile
                event={event}
                eventType={event.userIsEnrolled ? 'user' : 'available'}
                key={event.id}
              />
            )}
          </div>
        ))}
      </div>
      {data?.pages > 1 && !isHomeView ? (
        <Pagination
          currentPage={page}
          numberOfPages={data?.pages || 1}
          onPageChange={({ page }) => setPage(page)}
        />
      ) : showAllLinkHref && data?.eventsCount > data?.events?.length ? (
        <PageShowAllLink
          href={showAllLinkHref}
          numberOfItems={data?.eventsCount}
        />
      ) : null}
      <style jsx>{`
        .events {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 30px;
        }
        .event {
          width: 100%;
        }
        @media (min-width: ${breakpoints.small}px) {
          .event {
            width: calc(50% - 15px);
          }
        }
        @media (min-width: 900px) {
          .event {
            width: calc(33.333% - 20px);
          }
        }
        @media (min-width: 1100px) {
          .event {
            width: calc(25% - 22.5px);
          }
        }
      `}</style>
    </div>
  ) : hideIfNoData ? null : (
    <>
      <PageSectionHeader
        description={description}
        title={title || messages.commonAllCalendarEvents}
        titleCount={titleShowCount ? 0 : undefined}
      />

      <PageEmptyIndicator
        icon="tasks"
        title={messages.calendarEventsEmpty}
        description={messages.calendarEventsEmpty}
      />
    </>
  );
};

export default CalendarEventsViewAllCalendarEvents;
